import { IconCircleCheck } from '@tabler/icons-react';
import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { apiOrigin } from 'src/api/apiOrigin';
import { Anchor } from 'src/design/atoms/Anchor';
import { Center } from 'src/design/atoms/Center';
import { Loader } from 'src/design/atoms/Loader';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { ThemeIcon } from 'src/design/atoms/ThemeIcon';

// Must stay in sync with the segments the API emits in
// WorkflowAttachmentLinks.BuildDownloadUrl.
export const externalAttachmentProductTypes = [
  'change',
  'transmittal',
  'rfi',
  'submittal',
  'drawing',
] as const;

export type ExternalAttachmentProductType =
  typeof externalAttachmentProductTypes[number];

// A navigation that the browser turns into a file download fires no event on the
// page that started it, so the done state can only follow the redirect on a timer.
export const DOWNLOAD_STARTED_DELAY_MS = 1500;

const isExternalAttachmentProductType = (
  value: string | undefined,
): value is ExternalAttachmentProductType =>
  externalAttachmentProductTypes.includes(
    value as ExternalAttachmentProductType,
  );

export const ExternalAttachmentRedirect: React.FC = () => {
  const { productType, parentId, attachmentId } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const isAllowed = isExternalAttachmentProductType(productType);
  const [downloadStarted, setDownloadStarted] = React.useState(false);

  const target = `${apiOrigin}/external/${productType}/${parentId}/attachment/${attachmentId}?token=${encodeURIComponent(
    token,
  )}`;

  React.useEffect(() => {
    if (!isAllowed) {
      return;
    }
    window.location.replace(target);
    const timer = window.setTimeout(
      () => setDownloadStarted(true),
      DOWNLOAD_STARTED_DELAY_MS,
    );
    return () => window.clearTimeout(timer);
  }, [isAllowed, target]);

  if (!isAllowed) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Text>This link is not valid.</Text>
        </Stack>
      </Center>
    );
  }

  if (downloadStarted) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <ThemeIcon color="green" variant="light" size="xl" radius="xl">
            <IconCircleCheck />
          </ThemeIcon>
          <Text>Your download has started.</Text>
          <Text size="sm" c="dimmed">
            If nothing was downloaded,{' '}
            <Anchor href={target}>download it again</Anchor>.
          </Text>
        </Stack>
      </Center>
    );
  }

  return (
    <Center h="100vh">
      <Stack align="center" gap="md">
        <Loader />
        <Text>Preparing your download…</Text>
      </Stack>
    </Center>
  );
};
